import React from 'react';

import { CollapsibleRow } from './CollapsibleRow';
import styles from './styles.module.css';

import {
  type SizeFilterDepartment,
  type SizeFilterGroup,
  type SizeFilterRegion,
  type SizeFilterSize,
} from '@/modules/filters/aliases';
import {
  type SizeFilterDepartmentForCountry,
  type SizeFilterGroupForCountry,
} from '@/modules/filters/filterSizesByCountry';

type SizeFilterRowNode =
  | SizeFilterDepartment
  | SizeFilterGroup
  | SizeFilterRegion
  | SizeFilterDepartmentForCountry
  | SizeFilterGroupForCountry;

function childrenAreSizes(
  children: SizeFilterRowNode['children']
): children is SizeFilterSize[] {
  const [firstChild] = children;
  return Boolean(firstChild && 'composite_id' in firstChild);
}

export function SizeFilterRow({
  name,
  children,
  depth = 0,
  Leaf,
}: Pick<SizeFilterRowNode, 'name' | 'children'> & {
  depth?: number;
  Leaf: (props: { name: string; value: string }) => React.ReactNode;
}) {
  if (!children) {
    return null;
  }

  /**
   * If the children are leaf nodes (i.e. sizes) then we are
   * at full depth and render them inside a grid.
   */
  if (childrenAreSizes(children)) {
    return (
      <CollapsibleRow label={name ?? ''} depth={depth}>
        <div
          className={styles.sizeButtonsContainer}
          style={{ '--depth': depth } as React.CSSProperties}
        >
          {children.map(({ name: sizeName, composite_id }) => (
            <Leaf
              key={composite_id}
              name={sizeName ?? ''}
              value={composite_id}
            />
          ))}
        </div>
      </CollapsibleRow>
    );
  }

  /**
   * Otherwise recursively render children within collapsible dropdown rows.
   */
  return (
    <CollapsibleRow label={name ?? ''} depth={depth}>
      {children.map(({ id, name, children }) => (
        <SizeFilterRow
          key={`${name}-${id}`}
          name={name}
          depth={depth + 1}
          Leaf={Leaf}
          // eslint-disable-next-line react/no-children-prop
          children={children}
        />
      ))}
    </CollapsibleRow>
  );
}
